import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { favoriteGamesRequest } from 'api/casino';
import { AuthStatus, useClientProfile, useNotifications } from 'store';

type CasinoGamesContextType = {
  favoriteGames: Set<number>;
  addFavoriteGame: (gameId: number) => void;
  deleteFavoriteGame: (gameId: number) => void;
  loadFavoriteGames: (gameIds: Array<number>) => void;
  setFavoriteGames: Dispatch<SetStateAction<Set<number>>>;
};

const CasinoGamesContext = createContext<CasinoGamesContextType>(
  {} as CasinoGamesContextType,
);

const CasinoGamesProvider = ({ children }: PropsWithChildren) => {
  const [{ status, sessionToken }] = useClientProfile();
  const [favoriteGames, setFavoriteGames] = useState<Set<number>>(new Set([]));
  const [{ add: addNotification }] = useNotifications();

  const addFavoriteGame = useCallback((gameId: number) => {
    setFavoriteGames((prev) => {
      const newSet = new Set(prev);
      newSet.add(gameId);
      return newSet;
    });
  }, []);

  const deleteFavoriteGame = useCallback((gameId: number) => {
    setFavoriteGames((prev) => {
      const newSet = new Set(prev);
      newSet.delete(gameId);
      return newSet;
    });
  }, []);

  const loadFavoriteGames = useCallback(
    (gameIds: Array<number>) => {
      if (!sessionToken || !gameIds.length) return;

      favoriteGamesRequest({
        body: { gameIds },
        headers: { authorization: `Bearer ${sessionToken}` },
      })
        .then((data) => {
          if (data?.favoriteGames?.length) {
            setFavoriteGames(
              (prev) => new Set([...prev, ...data.favoriteGames]),
            );
          }
        })
        .catch(() => {
          addNotification({ text: 'something_went_wrong' });
        });
    },
    [sessionToken, addNotification],
  );

  useEffect(() => {
    if (status === AuthStatus.signedOut) {
      setFavoriteGames(new Set([]));
    }
  }, [status]);

  const value = useMemo(
    () => ({
      favoriteGames,
      addFavoriteGame,
      deleteFavoriteGame,
      setFavoriteGames,
      loadFavoriteGames,
    }),
    [favoriteGames, addFavoriteGame, deleteFavoriteGame, loadFavoriteGames],
  );

  return (
    <CasinoGamesContext.Provider value={value}>
      {children}
    </CasinoGamesContext.Provider>
  );
};

const useCasinoGames = () => useContext(CasinoGamesContext);

export { CasinoGamesProvider, useCasinoGames };
